/**
 * A napi naptár-idővonal ELHELYEZÉSI logikája — külön, render nélkül tesztelhető modulban.
 *
 * MI VOLT A BAJ (2026-09-13): a `DailyTimeline.tsx` csak azokat az eseményeket rajzolta ki,
 * amelyek kezdése PONTOSAN egy fél órás sávra esett (`eventStart === currentTime`), és a
 * hosszabb esemény által „lefedett" sávban induló eseményt egyáltalán nem jelenítette meg.
 * Következmény: a 09:15-kor induló esemény a napi nézetben LÁTHATATLAN volt (a heti nézetben
 * látszott), és a 10:00–11:00 melletti 10:30-as esemény is eltűnt. Ugyanez a hibaosztály az
 * edzői elérhetőség napi idővonalán már élesbe került és javítva lett
 * (`CoachAvailability/CoachAvailabilityDailyTimeline.tsx`) — itt ugyanaz a megközelítés:
 * PERCPONTOS pozicionálás + átfedő elhelyezés (hasábok), kiesés helyett.
 *
 * A modul szándékosan nem tud semmit a Reactről: a hibát a számolás okozta, tehát a számolást
 * kell őrizni.
 */

/** Egy fél órás rács-sor a bal oldali idő-oszlopban. */
export interface TimelineSlot {
  hour: number;
  minute: number;
  /** „09:30" — a sor felirata. */
  displayTime: string;
}

/** Idő-intervallum, amiből az elhelyezés számol. Csak ez a két mező kell. */
export interface TimeRange {
  startTime: string; // 'HH:mm' vagy 'HH:mm:ss'
  endTime: string;
}

/** Egy esemény kiszámolt helye a rácson. */
export interface PositionedEvent<T> {
  event: T;
  /** Pixel a rács tetejétől. */
  top: number;
  /** Pixel-magasság. */
  height: number;
  /** Hányadik hasáb az átfedő csoportján belül (0-tól). */
  columnIndex: number;
  /** Hány hasábra osztozik az átfedő csoportja. */
  columnCount: number;
}

/** Egy fél órás sor magassága pixelben. */
export const SLOT_HEIGHT = 40;
/** Egy óra magassága pixelben (két fél órás sor). */
export const HOUR_HEIGHT = SLOT_HEIGHT * 2;
/** A kártyák közti rés pixelben — enélkül a szomszédos kártyák szegélye összeér. */
const CARD_GAP = 4;
/** Ennél kisebb kártya már olvashatatlan (negyed órás esemény), ezért van alsó korlát. */
const MIN_CARD_HEIGHT = 28;

/** „09:15:00" → 9.25. Hibás/üres értékre 0. */
export function timeToHours(time?: string | null): number {
  if (!time) return 0;
  const [hourPart, minutePart] = time.split(':');
  const hours = Number(hourPart);
  const minutes = Number(minutePart ?? 0);
  if (Number.isNaN(hours)) return 0;
  return hours + (Number.isNaN(minutes) ? 0 : minutes) / 60;
}

/** Tizedes óra → „HH:mm". */
function toDisplayTime(hoursValue: number): string {
  const hour = Math.floor(hoursValue);
  const minute = Math.round((hoursValue - hour) * 60);
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
}

export interface DailyGrid {
  /** A rács első sorának órája (tizedes, pl. 8 vagy 6.5). */
  startHour: number;
  /** A rács VÉGE (kizárólagos), pl. 23. */
  endHour: number;
  slots: TimelineSlot[];
}

/** A nap vége tizedes órában. A rács SOSEM léphet túl ezen — lásd `buildDailyGrid`. */
const DAY_END_HOUR = 24;

/**
 * A rács sorai. Alapból 08:00–23:00, DE ha van a sávon kívülre eső esemény, a rács kinyílik
 * hozzá — így a napi nézet nem tud eseményt elrejteni (pont ez volt a hiba lényege).
 *
 * ⚠️ A rács a NAP VÉGÉNÉL MEGÁLL (2026-09-13-i javítás). Korábban nem: egy 23:45–23:59-es
 * eseménynél a fél órás minimum-hossz miatt a vég 24:15 lett, amit a felfelé kerekítés 24:30-ra
 * tolt, így a rács utolsó sorának a felirata `24:00` volt. Az a sor ugyanúgy kattintható, mint a
 * többi, és a kattintás `24:00:00` kezdéssel nyitotta az esemény-létrehozót → a szerver
 * `400`-at adott („The JSON value could not be converted to System.TimeOnly"), mert a
 * `TimeOnly` csak 00:00–23:59 között értelmes (a vég ráadásul 23:59 lett, tehát vég < kezdés).
 * A vágás NEM tünteti el a késő esti eseményt: a `layoutDailyEvents` a rács végéig kirajzolja
 * (minimum kártya-magassággal), tehát a 23:45-es kezdés továbbra is LÁTSZIK.
 */
export function buildDailyGrid(
  events: TimeRange[],
  defaultStartHour = 8,
  defaultEndHour = 23
): DailyGrid {
  let startHour = defaultStartHour;
  let endHour = defaultEndHour;

  events.forEach(event => {
    const start = timeToHours(event.startTime);
    const end = Math.max(timeToHours(event.endTime), start + 0.5);
    startHour = Math.min(startHour, Math.floor(start * 2) / 2);
    endHour = Math.max(endHour, Math.ceil(end * 2) / 2);
  });

  startHour = Math.max(0, startHour);
  endHour = Math.min(DAY_END_HOUR, endHour);

  const slots: TimelineSlot[] = [];
  for (let t = startHour; t < endHour; t += 0.5) {
    const hour = Math.floor(t);
    const minute = Math.round((t - hour) * 60);
    slots.push({ hour, minute, displayTime: toDisplayTime(t) });
  }

  return { startHour, endHour, slots };
}

/** Két intervallum átfedésben van-e (a zárt vég nem számít átfedésnek). */
function overlaps(a: { start: number; end: number }, b: { start: number; end: number }): boolean {
  return a.start < b.end && b.start < a.end;
}

/**
 * Percpontos elhelyezés + átfedés-kezelés. Ami átfed, az EGYMÁS MELLÉ kerül (hasábokba),
 * nem esik ki — a régi kód a „lefedett" sávban induló eseményt eldobta.
 */
export function layoutDailyEvents<T extends TimeRange>(
  events: T[],
  grid: DailyGrid,
  hourHeight: number = HOUR_HEIGHT
): PositionedEvent<T>[] {
  const spans = events.map(event => {
    const start = timeToHours(event.startTime);
    // Hibás/fordított vég (vagy nulla hosszú esemény) se tüntesse el a kártyát.
    const end = Math.max(timeToHours(event.endTime), start + 0.5);
    return { event, start, end };
  });

  // Kezdés szerint, azonos kezdésnél a hosszabb előre — így a hasáb-sorrend stabil.
  const sorted = [...spans].sort((a, b) => (a.start - b.start) || (b.end - a.end));

  // Átfedő csoportok (lánc is lehet: A–B átfed, B–C átfed, A–C nem).
  const groups: typeof sorted[] = [];
  sorted.forEach(span => {
    const group = groups.find(g => g.some(member => overlaps(span, member)));
    if (group) {
      group.push(span);
    } else {
      groups.push([span]);
    }
  });

  const positioned = new Map<T, PositionedEvent<T>>();
  groups.forEach(group => {
    group.forEach((span, columnIndex) => {
      const visibleStart = Math.max(span.start, grid.startHour);
      const visibleEnd = Math.min(span.end, grid.endHour);
      if (visibleEnd <= visibleStart) return; // teljesen a rácson kívül — nem fordulhat elő

      positioned.set(span.event, {
        event: span.event,
        top: (visibleStart - grid.startHour) * hourHeight,
        height: Math.max(MIN_CARD_HEIGHT, (visibleEnd - visibleStart) * hourHeight - CARD_GAP),
        columnIndex,
        columnCount: group.length,
      });
    });
  });

  // Az eredeti sorrendben adjuk vissza, hogy a render kulcsai stabilak legyenek.
  return events.map(event => positioned.get(event)).filter((p): p is PositionedEvent<T> => !!p);
}
